import { useEffect } from "react";
import type { Tier } from "@catchup/shared";
import { useStore } from "../store.ts";
import { buildRounds } from "../engine/round.ts";

const TIERS: Array<{ id: Tier; desc: string }> = [
  { id: "assisted", desc: "Full lyrics on screen, unsynced. No highlighting, no scroll cue." },
  { id: "faded", desc: "Lyrics shown, but each line vanishes just before you reach it." },
  { id: "blind", desc: "No lyrics at all. Just you and the song in your head." },
];

export function PickRound() {
  const store = useStore();
  const { chart, config, tier } = store;

  useEffect(() => {
    if (!chart) return;
    useStore.getState().set({ rounds: buildRounds(chart, config, tier) });
  }, [chart, config, tier]);

  if (!chart) return null;

  return (
    <>
      <div className="panel">
        <h2>Difficulty</h2>
        <p className="sub">The multiplier applies to your final score.</p>
        <div className="tiers">
          {TIERS.map((t) => (
            <button
              key={t.id}
              className={`tier ${tier === t.id ? "on" : ""}`}
              onClick={() => useStore.getState().set({ tier: t.id })}
            >
              <div className="name">{t.id}</div>
              <div className="mult">&times;{config.tierMultiplier[t.id].toFixed(1)}</div>
              <div className="desc">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="panel">
        <h2>Pick a section</h2>
        <p className="sub">
          You'll hear {config.capture.leadInMin}-{config.capture.leadInMax}s of lead-in,
          then the audio cuts and you carry on from memory.
        </p>

        {store.rounds.length === 0 ? (
          <p className="note warn">
            No playable sections in this chart. That usually means the lyrics are too
            sparse, or alignment failed and most words were dropped for low confidence.
          </p>
        ) : (
          <div className="rounds">
            {store.rounds.map((r, i) => (
              <button
                className="round-btn"
                key={i}
                onClick={() =>
                  useStore.getState().set({ round: r, screen: "play", result: null })
                }
              >
                <div>
                  <div>{r.firstLine || r.sectionLabel}</div>
                  <div className="meta">
                    {r.sectionLabel} · lead-in{" "}
                    {(r.spec.singStart - r.spec.leadInStart).toFixed(1)}s · sing{" "}
                    {(r.spec.singEnd - r.spec.singStart).toFixed(1)}s · {r.wordCount} words
                  </div>
                </div>
                <div className="spacer" />
                <span aria-hidden>→</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <button onClick={() => useStore.getState().set({ screen: "calibrate" })}>Back</button>
    </>
  );
}
